/**
 * Correção de um defeito de ARIA do `@vueform/multiselect`.
 *
 * O DEFEITO
 * ---------
 * Quando o combo é `searchable`, o pacote renderiza o `<input>` de busca com
 * `aria-placeholder`, `aria-multiselectable`, `aria-expanded`, `aria-activedescendant` e
 * `aria-controls` — mas SEM `role`. Lido do bundle (`multiselect.js`), a lista de
 * atributos do input de busca é `_hoisted_3`, e o `role: 'combobox'` só é aplicado no
 * caminho `!searchable`.
 *
 * `aria-multiselectable` não é permitido em `textbox` (o papel implícito de um `<input>`
 * de texto), e é isso que o axe acusa em `aria-allowed-attr` — a auditoria que faltava
 * para a tela de login fechar 100 na categoria Acessibilidade do Lighthouse.
 *
 * POR QUE CORRIGIR NO NAVEGADOR E NÃO NO TEMPLATE
 * -----------------------------------------------
 * Os atributos são gerados dentro do componente, a partir das props `placeholder` e
 * `mode`; nenhuma prop os desliga, e o pacote não expõe o input. As alternativas seriam
 * abandonar `searchable` (perde a busca, que é a razão de usar este combo) ou manter um
 * patch no `node_modules` (some no próximo `npm ci`).
 *
 * O QUE É REMOVIDO, E POR QUE É SEGURO
 * ------------------------------------
 * - `aria-multiselectable="false"`: valor padrão, não comunica nada. Num combo de escolha
 *   única é redundante por definição.
 * - `aria-placeholder`: duplica o `placeholder` nativo, que o próprio input já tem e que
 *   os leitores de tela leem.
 *
 * Os demais (`aria-expanded`, `aria-activedescendant`, `aria-controls`) NÃO são tocados:
 * são eles que informam o estado do combo, e removê-los tiraria informação de verdade
 * para fechar um número.
 *
 * CUSTO
 * -----
 * Um `MutationObserver` só em `childList`, e o retorno olha apenas os nós ACRESCENTADOS —
 * não varre o documento a cada mutação. A limpeza inicial cobre os combos que já estão na
 * tela quando isto roda.
 */

const ATRIBUTOS_INVALIDOS = ['aria-multiselectable', 'aria-placeholder'];
const SELETOR = '.multiselect-search[aria-multiselectable], .multiselect-search[aria-placeholder]';

function limpar(raiz: ParentNode): void {
  for (const campo of Array.from(raiz.querySelectorAll<HTMLElement>(SELETOR))) {
    for (const atributo of ATRIBUTOS_INVALIDOS) campo.removeAttribute(atributo);
  }
}

export function corrigirAriaDoMultiselect(): void {
  if (typeof document === 'undefined') return;

  limpar(document);

  const observador = new MutationObserver((mutacoes) => {
    for (const mutacao of mutacoes) {
      for (const no of Array.from(mutacao.addedNodes)) {
        if (!(no instanceof HTMLElement)) continue;
        // O próprio nó pode ser o input (combo montado sozinho) ou conter combos.
        if (no.matches?.(SELETOR)) {
          for (const atributo of ATRIBUTOS_INVALIDOS) no.removeAttribute(atributo);
        }
        limpar(no);
      }
    }
  });

  observador.observe(document.body, { childList: true, subtree: true });
}

export default { corrigirAriaDoMultiselect };
